const SMART_SEARCH_STOP_WORDS = new Set([
  "a",
  "an",
  "and",
  "are",
  "as",
  "at",
  "be",
  "but",
  "by",
  "for",
  "from",
  "how",
  "i",
  "in",
  "into",
  "is",
  "it",
  "me",
  "my",
  "of",
  "on",
  "or",
  "our",
  "please",
  "show",
  "that",
  "the",
  "their",
  "them",
  "these",
  "this",
  "to",
  "us",
  "we",
  "with",
  "you",
]);

const SMART_SEARCH_SYNONYM_GROUPS = [
  ["article", "blog", "post", "read", "story"],
  ["creator", "chef", "cook", "kitchen", "restaurant", "vendor"],
  ["deal", "discount", "offer", "promo"],
  ["dessert", "sweet", "treat"],
  ["dish", "entree", "food", "meal", "plate"],
  ["driver", "courier", "delivery", "rider"],
  ["drink", "beverage", "juice", "mocktail", "smoothie", "soda"],
  ["help", "issue", "problem", "support"],
  ["meal", "dish", "food", "main", "plate"],
  ["order", "basket", "cart", "purchase"],
  ["recipe", "cook", "cooking"],
  ["staff", "crew", "employee", "team"],
  ["story", "article", "blog", "post"],
  ["video", "clip", "reel", "watch"],
].map((group) => group.map((entry) => entry.toLowerCase()));

const SMART_SEARCH_SYNONYMS: Record<string, string[]> = SMART_SEARCH_SYNONYM_GROUPS.reduce<
  Record<string, string[]>
>((accumulator, group) => {
  group.forEach((entry) => {
    accumulator[entry] = group.filter((candidate) => candidate !== entry);
  });
  return accumulator;
}, {});

const SMART_SEARCH_QUERY_CACHE_LIMIT = 200;
const smartSearchPreparedQueryCache = new Map<string, SmartSearchPreparedQuery>();

type SmartSearchPreparedQuery = {
  normalized: string;
  compact: string;
  tokens: string[];
  tokenVariants: string[][];
  initials: string;
};

type SmartSearchPreparedCorpus = {
  normalizedValues: string[];
  compactValues: string[];
  joinedText: string;
  compactText: string;
  wordTokens: string[];
  wordTokenSet: Set<string>;
  initials: string;
};

const rememberPreparedQuery = (
  preparedQuery: SmartSearchPreparedQuery,
) => {
  if (smartSearchPreparedQueryCache.has(preparedQuery.normalized)) {
    const cached = smartSearchPreparedQueryCache.get(preparedQuery.normalized);
    if (cached) {
      return cached;
    }
  }

  smartSearchPreparedQueryCache.set(preparedQuery.normalized, preparedQuery);
  if (smartSearchPreparedQueryCache.size > SMART_SEARCH_QUERY_CACHE_LIMIT) {
    const oldestKey = smartSearchPreparedQueryCache.keys().next().value;
    if (typeof oldestKey === "string") {
      smartSearchPreparedQueryCache.delete(oldestKey);
    }
  }

  return preparedQuery;
};

export const normalizeSmartSearchText = (value: string) =>
  String(value)
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/&/g, " and ")
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/['’]/g, "")
    .replace(/[^a-z0-9]+/g, " ")
    .trim()
    .replace(/\s+/g, " ");

const compactSmartSearchText = (value: string) =>
  normalizeSmartSearchText(value).replace(/\s+/g, "");

const toSearchValues = (value: unknown): string[] => {
  if (typeof value === "string" || typeof value === "number") {
    return [String(value)];
  }

  if (Array.isArray(value)) {
    return value.flatMap((entry) => toSearchValues(entry));
  }

  if (value && typeof value === "object") {
    return Object.values(value as Record<string, unknown>).flatMap((entry) =>
      toSearchValues(entry),
    );
  }

  return [];
};

const singularizeSmartSearchToken = (token: string) => {
  if (token.endsWith("ies") && token.length > 4) {
    return `${token.slice(0, -3)}y`;
  }
  if (token.endsWith("oes") && token.length > 4) {
    return token.slice(0, -2);
  }
  if (token.endsWith("es") && token.length > 4) {
    return token.slice(0, -2);
  }
  if (token.endsWith("s") && token.length > 3) {
    return token.slice(0, -1);
  }
  return token;
};

export const tokenizeSmartSearch = (
  value: string,
  options?: {
    minTokenLength?: number;
    removeStopWords?: boolean;
  },
) => {
  const minTokenLength = options?.minTokenLength ?? 2;
  const removeStopWords = options?.removeStopWords ?? true;

  return Array.from(
    new Set(
      normalizeSmartSearchText(value)
        .split(" ")
        .map((token) => token.trim())
        .filter((token) => token.length >= minTokenLength)
        .map((token) => singularizeSmartSearchToken(token))
        .filter((token) =>
          removeStopWords ? !SMART_SEARCH_STOP_WORDS.has(token) : true,
        ),
    ),
  );
};

const expandSmartSearchToken = (token: string) => {
  const singular = singularizeSmartSearchToken(token);
  const normalized = normalizeSmartSearchText(token);
  const compact = compactSmartSearchText(token);

  return Array.from(
    new Set(
      [
        token,
        singular,
        normalized,
        compact,
        ...(SMART_SEARCH_SYNONYMS[token] || []),
        ...(SMART_SEARCH_SYNONYMS[singular] || []),
        ...(SMART_SEARCH_SYNONYMS[normalized] || []),
      ]
        .map((value) => normalizeSmartSearchText(value))
        .filter(Boolean),
    ),
  );
};

const buildSmartSearchInitials = (values: readonly string[]) =>
  values
    .flatMap((value) =>
      value
        .split(" ")
        .map((word) => word[0] || "")
        .filter(Boolean),
    )
    .join("");

export const prepareSmartSearchQuery = (query: string): SmartSearchPreparedQuery => {
  const normalized = normalizeSmartSearchText(query);
  if (!normalized) {
    return {
      normalized: "",
      compact: "",
      tokens: [],
      tokenVariants: [],
      initials: "",
    };
  }

  const cached = smartSearchPreparedQueryCache.get(normalized);
  if (cached) {
    return cached;
  }

  const tokens = tokenizeSmartSearch(normalized);
  const fallbackTokens = tokens.length > 0 ? tokens : [normalized];
  const prepared = rememberPreparedQuery({
    normalized,
    compact: normalized.replace(/\s+/g, ""),
    tokens: fallbackTokens,
    tokenVariants: fallbackTokens.map((token) => expandSmartSearchToken(token)),
    initials: buildSmartSearchInitials([normalized]),
  });

  return prepared;
};

const buildSmartSearchCorpus = (
  values: ReadonlyArray<unknown>,
): SmartSearchPreparedCorpus => {
  const normalizedValues = Array.from(
    new Set(
      values
        .flatMap((value) => toSearchValues(value))
        .map((value) => normalizeSmartSearchText(value))
        .filter(Boolean),
    ),
  );
  const compactValues = normalizedValues.map((value) => value.replace(/\s+/g, ""));
  const joinedText = normalizedValues.join(" ");
  const compactText = compactValues.join("");
  const wordTokens = Array.from(new Set(joinedText.split(" ").filter(Boolean)));

  return {
    normalizedValues,
    compactValues,
    joinedText,
    compactText,
    wordTokens,
    wordTokenSet: new Set(wordTokens),
    initials: buildSmartSearchInitials(normalizedValues),
  };
};

const isEditDistanceAtMostOne = (left: string, right: string) => {
  if (left === right) {
    return true;
  }

  const leftLength = left.length;
  const rightLength = right.length;
  if (Math.abs(leftLength - rightLength) > 1) {
    return false;
  }

  if (leftLength === rightLength) {
    let mismatches = 0;
    for (let index = 0; index < leftLength; index += 1) {
      if (left[index] === right[index]) {
        continue;
      }

      mismatches += 1;
      if (mismatches > 2) {
        return false;
      }

      if (
        mismatches === 2 &&
        !(
          index > 0 &&
          left[index] === right[index - 1] &&
          left[index - 1] === right[index]
        )
      ) {
        return false;
      }
    }

    return true;
  }

  const shorter = leftLength < rightLength ? left : right;
  const longer = leftLength < rightLength ? right : left;
  let shorterIndex = 0;
  let longerIndex = 0;
  let mismatches = 0;

  while (shorterIndex < shorter.length && longerIndex < longer.length) {
    if (shorter[shorterIndex] === longer[longerIndex]) {
      shorterIndex += 1;
      longerIndex += 1;
      continue;
    }

    mismatches += 1;
    if (mismatches > 1) {
      return false;
    }
    longerIndex += 1;
  }

  return true;
};

const getTokenVariantMatchScore = (
  tokenVariants: readonly string[],
  corpus: SmartSearchPreparedCorpus,
) => {
  let bestScore = 0;

  for (const variant of tokenVariants) {
    if (!variant) continue;

    if (corpus.wordTokenSet.has(variant)) {
      return 38 + Math.min(variant.length, 10);
    }

    if (corpus.normalizedValues.some((value) => value === variant)) {
      bestScore = Math.max(bestScore, 34 + Math.min(variant.length, 8));
      continue;
    }

    if (corpus.wordTokens.some((word) => word.startsWith(variant))) {
      bestScore = Math.max(bestScore, 28 + Math.min(variant.length, 8));
      continue;
    }

    if (corpus.compactValues.some((value) => value.startsWith(variant))) {
      bestScore = Math.max(bestScore, 24 + Math.min(variant.length, 7));
      continue;
    }

    if (corpus.joinedText.includes(variant) || corpus.compactText.includes(variant)) {
      bestScore = Math.max(bestScore, 18 + Math.min(variant.length, 6));
      continue;
    }

    if (
      variant.length >= 4 &&
      corpus.wordTokens.some((word) =>
        Math.abs(word.length - variant.length) <= 1 &&
        word[0] === variant[0] &&
        isEditDistanceAtMostOne(word, variant),
      )
    ) {
      bestScore = Math.max(bestScore, 16 + Math.min(variant.length, 5));
    }
  }

  return bestScore;
};

export const countSmartSearchTokenMatches = (
  query: string | SmartSearchPreparedQuery,
  values: ReadonlyArray<unknown>,
) => {
  const preparedQuery =
    typeof query === "string" ? prepareSmartSearchQuery(query) : query;
  if (!preparedQuery.normalized) {
    return 0;
  }

  const corpus = buildSmartSearchCorpus(values);
  if (corpus.normalizedValues.length === 0) {
    return 0;
  }

  return preparedQuery.tokenVariants.reduce(
    (count, tokenVariants) =>
      count + (getTokenVariantMatchScore(tokenVariants, corpus) > 0 ? 1 : 0),
    0,
  );
};

export const scoreSmartSearchValues = (
  query: string | SmartSearchPreparedQuery,
  values: ReadonlyArray<unknown>,
) => {
  const preparedQuery =
    typeof query === "string" ? prepareSmartSearchQuery(query) : query;
  if (!preparedQuery.normalized) {
    return 0;
  }

  const corpus = buildSmartSearchCorpus(values);
  if (corpus.normalizedValues.length === 0) {
    return 0;
  }

  let score = 0;

  if (corpus.normalizedValues.includes(preparedQuery.normalized)) {
    score += 260;
  } else if (corpus.compactValues.includes(preparedQuery.compact)) {
    score += 220;
  } else if (corpus.normalizedValues.some((value) => value.startsWith(preparedQuery.normalized))) {
    score += 170;
  } else if (preparedQuery.compact && corpus.compactText.includes(preparedQuery.compact)) {
    score += 130;
  } else if (corpus.joinedText.includes(preparedQuery.normalized)) {
    score += 105;
  }

  let matchedTokens = 0;
  let exactishTokenMatches = 0;

  preparedQuery.tokenVariants.forEach((tokenVariants) => {
    const tokenScore = getTokenVariantMatchScore(tokenVariants, corpus);
    if (tokenScore > 0) {
      matchedTokens += 1;
      if (tokenScore >= 30) {
        exactishTokenMatches += 1;
      }
      score += tokenScore;
    }
  });

  if (preparedQuery.tokens.length > 1) {
    const orderedTokens = preparedQuery.tokens.join(" ");
    if (corpus.joinedText.includes(orderedTokens)) {
      score += 48;
    }

    if (matchedTokens === preparedQuery.tokens.length) {
      score += 60;
    } else if (matchedTokens >= preparedQuery.tokens.length - 1) {
      score += 24;
    }
  }

  if (
    corpus.initials &&
    preparedQuery.initials.length > 1 &&
    corpus.initials.startsWith(preparedQuery.initials)
  ) {
    score += 32;
  }

  if (exactishTokenMatches === preparedQuery.tokens.length && preparedQuery.tokens.length > 0) {
    score += 18;
  }

  return score;
};

export const createSmartSearchScorer = (query: string) => {
  const preparedQuery = prepareSmartSearchQuery(query);
  return (values: ReadonlyArray<unknown>) =>
    scoreSmartSearchValues(preparedQuery, values);
};

export const matchesSmartSearch = (
  query: string | SmartSearchPreparedQuery,
  values: ReadonlyArray<unknown>,
  options?: { minScore?: number },
) => scoreSmartSearchValues(query, values) >= (options?.minScore ?? 1);

export const rankSmartSearchResults = <T>(
  items: readonly T[],
  query: string,
  getValues: (item: T) => ReadonlyArray<unknown>,
  options?: {
    limit?: number;
    minScore?: number;
  },
) => {
  const preparedQuery = prepareSmartSearchQuery(query);
  if (!preparedQuery.normalized) {
    return [];
  }

  const ranked = items
    .map((item, index) => ({
      item,
      score: scoreSmartSearchValues(preparedQuery, getValues(item)) - index * 0.001,
    }))
    .filter((entry) => entry.score >= (options?.minScore ?? 1))
    .sort((left, right) => right.score - left.score);

  const limit = options?.limit;
  return typeof limit === "number" ? ranked.slice(0, limit) : ranked;
};
